import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { X, Plus, Loader2, Search } from "lucide-react";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";

/**
 * Wave 3 (Nikhil, 2026-09-30) — staff-only "New NJM Collaboration" admin form. Deliberately a
 * SEPARATE component from the freelancer-facing `CreateCollaborationModal` (never reused there)
 * — per the founder's explicit call, staff create these from Admin → Opportunities only, and
 * this is the ONLY place the Budget block / role fee-per-role / Project link fields exist.
 * Always Paid (`kind: "njm"` on create, forced server-side too) — no compensation picker here.
 * The Total/Planned/Committed/Remaining figures are STAFF-ONLY and fetched from their own
 * endpoint (`GET .../admin-budget`) — never bundled with the freelancer-facing opportunity
 * payload, so they can never leak through this form or anywhere else.
 */
const LOCATION_TYPES = ["Remote", "Hybrid", "On-site"];
const WORK_MODE_TO_LOCATION_TYPE = { onsite: "On-site", remote: "Remote", hybrid: "Hybrid" };
const PROJECT_TYPES = ["Short Film", "Music Video", "Commercial/Ad", "Documentary", "Wedding", "Brand Content", "Event Coverage", "Personal Project", "Other"];
const MAX_SLOTS = 10;
const emptySlot = () => ({ slot_id: null, role: "", quantity_required: 1, role_description: "", quantity_filled: 0, fee_amount: "", fee_tbd: false });

const label = "font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint";
const input = "mt-1.5 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors";
const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

/**
 * `initialFromOpportunity` — Wave 4 (2026-09-30) "Manage through NJM": a plain External
 * Opportunity object (title/discipline/city/work_mode/public_summary) used to PREFILL a brand
 * new NJM Collaboration, never to edit one — ignored entirely whenever `initialOpportunity` is
 * also set (edit always wins). Staff still fill in roles/quantities/fees/budget themselves,
 * same as any other create; this only saves retyping the basics. The two are linked both ways
 * server-side on submit (`source_opportunity_id` in the create payload) — nothing here copies
 * `internal{}` (contact/notes), which this component never even receives.
 */
export function CreateNjmCollaborationModal({ initialOpportunity, initialFromOpportunity, onClose, onSaved }) {
  const isEdit = Boolean(initialOpportunity);
  const seed = !isEdit ? initialFromOpportunity : null;
  const [roleOptions, setRoleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectQuery, setProjectQuery] = useState("");
  const [projectId, setProjectId] = useState("");
  const [showProjectResults, setShowProjectResults] = useState(false);
  const [committedRemaining, setCommittedRemaining] = useState(null);
  // Fix (2026-10, code review) — `initialOpportunity` comes from the freelancer-safe
  // `GET /opportunities/{id}` view, which never includes `total_talent_budget`/`project_id`
  // (staff-only, deliberately kept out of that DTO). Seeding this form from it meant editing
  // ALWAYS wiped both fields. `adminDataLoaded` gates the submit payload below so a save can
  // never fire before the real values (fetched from the staff-only /admin-budget endpoint)
  // have loaded — until then, these two keys are simply omitted from the PATCH body.
  const [adminDataLoaded, setAdminDataLoaded] = useState(!isEdit);
  // Code-review fix (2026-10, Cursor audit item 1): the fetch above used to flip
  // `adminDataLoaded` true in a `.finally()` — i.e. even when the request FAILED — which
  // re-enabled Save with `totalBudget`/`projectId` still at their empty init values, silently
  // submitting nulls over the real budget/project link. Now the flag is set true ONLY on
  // success; a failure shows a Retry affordance and keeps Save disabled.
  const [adminDataError, setAdminDataError] = useState(false);
  // Code-review fix (2026-10, Cursor audit P4) — tracks whether the admin-budget fetch found
  // an EXISTING value, so submit() can tell "always empty" apart from "user just cleared a
  // real value" and only send the explicit clear_* flag in the latter case.
  const [budgetHadValue, setBudgetHadValue] = useState(false);
  const [projectHadValue, setProjectHadValue] = useState(false);

  const [projectName, setProjectName] = useState(initialOpportunity?.project_name || seed?.title || "");
  const [description, setDescription] = useState(initialOpportunity?.description || seed?.public_summary || "");
  const [city, setCity] = useState(initialOpportunity?.city || seed?.city || seed?.location_label || "");
  const [locationType, setLocationType] = useState(initialOpportunity?.location_type || WORK_MODE_TO_LOCATION_TYPE[seed?.work_mode] || "");
  const [projectType, setProjectType] = useState(initialOpportunity?.project_type || "");
  const [timeline, setTimeline] = useState(initialOpportunity?.timeline || "");
  const [creatorRole, setCreatorRole] = useState(initialOpportunity?.creator_role || "");
  const [totalBudget, setTotalBudget] = useState("");
  const [slots, setSlots] = useState(
    initialOpportunity?.role_slots?.length
      ? initialOpportunity.role_slots.map((s) => ({ slot_id: s.slot_id, role: s.role, quantity_required: s.quantity_required, role_description: s.role_description || "", quantity_filled: s.quantity_filled || 0, fee_amount: s.fee_amount || "", fee_tbd: Boolean(s.fee_tbd) }))
      : [emptySlot()]
  );
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const guard = useEmailGuard();

  useEffect(() => {
    api.get("/v1/build/roles").then((r) => {
      const opts = r.data.items || [];
      setRoleOptions(opts);
      // Discipline and the collaboration role taxonomy are the SAME list (role_branches.json
      // BRANCHES keys) — a direct match is always exact when there is one, never fuzzy.
      const suggested = (seed?.discipline || []).find((d) => opts.includes(d));
      if (suggested) setSlots((prev) => (prev.length === 1 && !prev[0].role ? [{ ...prev[0], role: suggested }] : prev));
    }).catch(() => {});
    api.get("/v1/build/locations").then((r) => setCityOptions(r.data.items || [])).catch(() => {});
    api.get("/v1/projects/picker").then((r) => setProjects(r.data.items || [])).catch(() => {});
    if (isEdit) loadAdminBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAdminBudget = () => {
    setAdminDataError(false);
    api.get(`/v1/collaboration/opportunities/${initialOpportunity.id}/admin-budget`)
      .then((r) => {
        setCommittedRemaining(r.data.budget);
        if (r.data.budget?.total_talent_budget != null) { setTotalBudget(r.data.budget.total_talent_budget); setBudgetHadValue(true); }
        if (r.data.project_id) { setProjectId(r.data.project_id); setProjectQuery(r.data.project_name || ""); setProjectHadValue(true); }
        setAdminDataLoaded(true);
      })
      .catch(() => setAdminDataError(true));
  };

  useEffect(() => {
    if (projectId && projects.length && !projectQuery) {
      const p = projects.find((pr) => pr.id === projectId);
      if (p) setProjectQuery(p.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const updateSlot = (i, patch) => setSlots(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSlot = () => slots.length < MAX_SLOTS && setSlots([...slots, emptySlot()]);
  const removeSlot = (i) => {
    if (slots[i].quantity_filled > 0) {
      toast.error("Can't remove — this role already has accepted members");
      return;
    }
    setSlots(slots.filter((_, idx) => idx !== i));
  };

  const validSlots = slots.filter((s) => s.role && s.quantity_required >= 1);
  const plannedBudget = validSlots.reduce((sum, s) => sum + (s.fee_tbd ? 0 : Number(s.fee_amount || 0) * Number(s.quantity_required || 0)), 0);
  const committed = committedRemaining?.committed ?? 0;
  const remaining = totalBudget !== "" && totalBudget !== null ? Number(totalBudget) - committed : null;

  const filteredProjects = projectQuery.trim() ? projects.filter((p) => (p.name || "").toLowerCase().includes(projectQuery.trim().toLowerCase())).slice(0, 8) : [];

  const validate = () => {
    const e = {};
    if (!projectName.trim()) e.projectName = "Required";
    else if (projectName.length > 120) e.projectName = "Keep it under 120 characters";
    if (!description.trim()) e.description = "Required";
    else if (description.length > 2000) e.description = "Keep it under 2000 characters";
    if (!creatorRole) e.creatorRole = "Pick a role";
    if (validSlots.length === 0) e.slots = "Add at least one role";
    if (validSlots.some((s) => !s.fee_tbd && !s.fee_amount)) e.fees = "Set a fee (INR) or mark \"Fee to be discussed\" for every role";
    if (validSlots.some((s) => (s.role_description || "").length > 500)) e.roleDescription = "Keep each role description under 500 characters";
    if (timeline.length > 120) e.timeline = "Keep it under 120 characters";
    return e;
  };

  const submit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Fix the highlighted fields");
      return;
    }
    setErrors({});
    const payload = {
      project_name: projectName.trim(), description: description.trim(),
      city: city || null, location_type: locationType || null, project_type: projectType || null,
      timeline: timeline.trim() || null, creator_role: creatorRole,
      role_slots: validSlots.map((s) => ({
        ...(s.slot_id ? { slot_id: s.slot_id } : {}),
        role: s.role, quantity_required: Number(s.quantity_required),
        role_description: (s.role_description || "").trim() || null,
        fee_amount: !s.fee_tbd && s.fee_amount ? Number(s.fee_amount) : null,
        fee_tbd: Boolean(s.fee_tbd),
      })),
      total_talent_budget: totalBudget === "" ? null : Number(totalBudget),
      project_id: projectId || null,
    };
    // Code-review fix (2026-10, Cursor audit P4) — clearing a budget/project link the admin
    // fetch actually found a value for now sends an EXPLICIT clear flag (the backend ignores a
    // bare null/omitted key, see collaboration.py::update_opportunity); if the field was never
    // loaded with a value, just omit the key instead of asserting an intentional clear.
    if (isEdit) {
      if (totalBudget === "") { if (budgetHadValue) payload.clear_total_talent_budget = true; delete payload.total_talent_budget; }
      if (!projectId) { if (projectHadValue) payload.clear_project_id = true; delete payload.project_id; }
    }
    if (!isEdit) {
      payload.kind = "njm";
      if (seed?.id) payload.source_opportunity_id = seed.id;
    }
    // Fix (2026-10, code review, Issue 1): while editing, these two keys must be OMITTED
    // entirely from the PATCH body until the real values have loaded from the staff-only
    // /admin-budget endpoint — the backend treats a PRESENT key as "set this", so sending them
    // early (before `adminDataLoaded`) would wipe total_talent_budget/project_id back to null.
    if (isEdit && !adminDataLoaded) {
      delete payload.total_talent_budget;
      delete payload.project_id;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/v1/collaboration/opportunities/${initialOpportunity.id}`, payload);
        toast.success("NJM Collaboration updated");
      } else {
        // Email Send Guard (2026-02): see CreateCollaborationModal — same two-step confirm for
        // a founder/partner/pm actor. L (fix pass 2): coordinator/finance/creative CAN create
        // an NJM Collaboration (community.moderate) but lack community.announce — their blast
        // is QUEUED for a founder/partner/pm to review, never blocked/403'd and never sent
        // directly; `email_pending_approval` on the response is the only signal of that.
        const r = await api.post("/v1/collaboration/opportunities", payload);
        if (r.data?.opportunity?.email_pending_approval) {
          toast.success("NJM Collaboration created", {
            description: "Email pending approval — a founder/partner/PM will review before notifying members.",
          });
        } else if (guard.intercept(r.data, async (typedCount) => {
          await api.post("/v1/collaboration/opportunities", { ...payload, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
          toast.success("NJM Collaboration created");
          onSaved();
        })) {
          return;
        } else {
          toast.success("NJM Collaboration created");
        }
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      if (detail.code === "invalid_role") toast.error("Pick a role from the list for every field");
      else if (detail.code === "too_many_slots") toast.error(`Up to ${MAX_SLOTS} roles per collaboration`);
      else if (detail.code === "field_too_long") setErrors({ [detail.field === "role_description" ? "roleDescription" : detail.field]: `Keep it under ${detail.max} characters` });
      else if (detail.code === "fee_required_for_paid_role") toast.error(`Set a fee or "Fee to be discussed" for ${detail.role || "every role"}`);
      else if (detail.code === "invalid_fee_amount") toast.error("That fee amount doesn't look right");
      else if (detail.code === "invalid_budget") toast.error("That total talent budget doesn't look right");
      else if (detail.code === "invalid_project_id") toast.error("Couldn't find that linked project");
      else if (detail.code === "invalid_source_opportunity_id") toast.error("Couldn't find the source opportunity to link");
      else if (detail.code === "already_promoted_to_open_collaboration") toast.error("This opportunity is already managed through an open NJM Collaboration");
      else if (detail.code === "only_staff_can_create_njm_collaboration") toast.error("Only staff can create an NJM Collaboration");
      else if (detail.code === "slot_below_accepted") toast.error("That role already has accepted members — quantity can't go below that");
      else if (detail.code === "slot_has_applications") toast.error("That role already has applicants — remove is blocked to protect them");
      else if (detail.code === "confirmation_required") toast.error("That confirmation expired — try again");
      else toast.error(isEdit ? "Couldn't save changes" : "Couldn't create this collaboration");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose} data-testid="create-njm-collaboration-overlay">
      <div className="w-full max-w-2xl rounded-2xl bg-paper shadow-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 bg-ink border-t-[3px] border-brand px-6 sm:px-7 py-5 flex items-start justify-between">
          <div>
            <div className="font-mont text-[9.5px] font-extrabold uppercase tracking-[0.16em] text-brand">{isEdit ? "Edit NJM Collaboration" : "New NJM Collaboration"}</div>
            <div className="mt-1.5 font-mont text-lg font-bold text-paper">Staff-created · Always Paid</div>
            {seed && (
              <div className="mt-1 font-mont text-[11px] font-semibold text-brand" data-testid="create-njm-collaboration-seed-note">
                Prefilled from External Opportunity: {seed.title}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="create-njm-collaboration-close" className="text-paper/60 hover:text-paper transition-colors text-lg leading-none">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 sm:px-7 py-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className={label}>Title</label>
              <input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. NJM House Brand Shoot" data-testid="create-njm-collaboration-project-name"
                className={`${input} font-bold text-base ${errors.projectName ? "border-red-400" : ""}`} />
              {errors.projectName && <div className="mt-1 text-[11px] text-red-500" data-testid="create-njm-collaboration-project-name-error">{errors.projectName}</div>}
            </div>
            <div>
              <label className={label}>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What is this, and what do we need?" data-testid="create-njm-collaboration-description"
                className={`${input} resize-none ${errors.description ? "border-red-400" : ""}`} />
              {errors.description && <div className="mt-1 text-[11px] text-red-500" data-testid="create-njm-collaboration-description-error">{errors.description}</div>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={label}>City</label>
                <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="create-njm-collaboration-city" className={`${input} font-semibold cursor-pointer`}>
                  <option value="">Not specified</option>
                  {city && !cityOptions.includes(city) && <option value={city}>{city}</option>}
                  {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Location Type</label>
                <select value={locationType} onChange={(e) => setLocationType(e.target.value)} data-testid="create-njm-collaboration-location-type" className={`${input} font-semibold cursor-pointer`}>
                  <option value="">Not specified</option>
                  {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Timeline <span className="text-hairline-strong">optional</span></label>
                <input value={timeline} onChange={(e) => setTimeline(e.target.value)} placeholder="Shoot in 3 weeks" data-testid="create-njm-collaboration-timeline" className={`${input} font-semibold ${errors.timeline ? "border-red-400" : ""}`} />
              </div>
            </div>
            {errors.timeline && <div className="text-[11px] text-red-500 -mt-2">{errors.timeline}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={label}>Project Type <span className="text-hairline-strong">optional</span></label>
                <select value={projectType} onChange={(e) => setProjectType(e.target.value)} data-testid="create-njm-collaboration-project-type" className={`${input} font-semibold cursor-pointer`}>
                  <option value="">Not specified</option>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>NJM Lead Role</label>
                <select value={creatorRole} onChange={(e) => setCreatorRole(e.target.value)} data-testid="create-njm-collaboration-creator-role" className={`${input} font-semibold cursor-pointer ${errors.creatorRole ? "border-red-400" : ""}`}>
                  <option value="">Select role</option>
                  {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {errors.creatorRole && <div className="mt-1 text-[11px] text-red-500" data-testid="create-njm-collaboration-creator-role-error">{errors.creatorRole}</div>}
              </div>
            </div>
          </div>

          <div className="h-px bg-black/8" />

          <div>
            <label className={label}>Link to Project <span className="text-hairline-strong">optional · display only, no Assignment created</span></label>
            <div className="relative mt-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
              <input value={projectQuery}
                onChange={(e) => { setProjectQuery(e.target.value); setShowProjectResults(true); if (!e.target.value) setProjectId(""); }}
                onFocus={() => setShowProjectResults(true)}
                placeholder="Search projects…" data-testid="create-njm-collaboration-project-search"
                className="w-full rounded-lg border border-black/10 bg-paper pl-9 pr-8 py-2.5 font-mont text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink" />
              {projectId && (
                <button type="button" onClick={() => { setProjectId(""); setProjectQuery(""); }} data-testid="create-njm-collaboration-project-clear"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-faint hover:text-ink"><X className="h-3.5 w-3.5" /></button>
              )}
              {showProjectResults && filteredProjects.length > 0 && !projectId && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-black/10 bg-paper shadow-lg max-h-48 overflow-y-auto" data-testid="create-njm-collaboration-project-results">
                  {filteredProjects.map((p) => (
                    <button key={p.id} type="button" onClick={() => { setProjectId(p.id); setProjectQuery(p.name); setShowProjectResults(false); }} data-testid={`create-njm-collaboration-project-option-${p.id}`}
                      className="block w-full text-left px-3.5 py-2 font-mont text-xs text-ink hover:bg-surface2/60">{p.name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-black/8" />

          <div>
            <div className="flex items-center justify-between">
              <span className="font-mont text-[10px] font-extrabold uppercase tracking-[0.16em] text-ink">Roles Needed</span>
              <span className="inline-flex items-center rounded-full bg-ink px-2.5 py-1 font-mont text-[9.5px] font-extrabold uppercase tracking-wider text-brand">Paid</span>
            </div>
            <div className="mt-3 space-y-2.5">
              {slots.map((s, i) => (
                <div key={i} className="rounded-xl border border-black/10 border-l-[3px] border-l-brand bg-paper p-3.5" data-testid={`create-njm-collaboration-slot-${i}`}>
                  <div className="flex items-center gap-3">
                    <select value={s.role} onChange={(e) => updateSlot(i, { role: e.target.value })} data-testid={`create-njm-collaboration-slot-role-${i}`}
                      className="flex-1 min-w-0 font-mont text-sm font-bold text-ink bg-transparent focus:outline-none cursor-pointer">
                      <option value="">Select a role</option>
                      {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {slots.length > 1 && (
                      <button onClick={() => removeSlot(i)} data-testid={`create-njm-collaboration-slot-remove-${i}`} className="shrink-0 font-mont text-[11px] font-semibold text-red-500 hover:text-red-600">Remove</button>
                    )}
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <select value={s.quantity_required} onChange={(e) => updateSlot(i, { quantity_required: Number(e.target.value) })} data-testid={`create-njm-collaboration-slot-qty-${i}`}
                      className="rounded-full border border-black/12 bg-surface2/60 px-3 py-1.5 font-mont text-[10.5px] font-semibold text-ink-soft focus:outline-none cursor-pointer">
                      {Array.from({ length: 20 }).map((_, n) => n + 1).filter((n) => n >= (s.quantity_filled || 1)).map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
                    </select>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mont text-[11px] text-faint">₹</span>
                      <input type="number" min="1" value={s.fee_amount} disabled={s.fee_tbd} onChange={(e) => updateSlot(i, { fee_amount: e.target.value })} placeholder="Fee per person" data-testid={`create-njm-collaboration-slot-fee-amount-${i}`}
                        className="w-36 rounded-full border border-black/12 bg-paper pl-6 pr-3 py-1.5 font-mont text-[10.5px] font-semibold text-ink placeholder:text-faint focus:outline-none focus:border-ink disabled:opacity-50 disabled:bg-surface2/40" />
                    </div>
                    <label className="inline-flex items-center gap-1.5 font-mont text-[10.5px] font-medium text-ink-soft cursor-pointer">
                      <input type="checkbox" checked={s.fee_tbd} onChange={(e) => updateSlot(i, { fee_tbd: e.target.checked, fee_amount: e.target.checked ? "" : s.fee_amount })} data-testid={`create-njm-collaboration-slot-fee-tbd-${i}`} className="accent-brand" />
                      Fee to be discussed
                    </label>
                  </div>
                  <textarea value={s.role_description} onChange={(e) => updateSlot(i, { role_description: e.target.value })} placeholder="What does this role involve? (optional)" rows={2} data-testid={`create-njm-collaboration-slot-description-${i}`}
                    className="mt-2.5 w-full rounded-lg border border-dashed border-black/12 bg-transparent px-3 py-2 font-mont text-[11px] text-ink placeholder:text-faint focus:outline-none focus:border-black/30 resize-none" />
                </div>
              ))}
              {slots.length < MAX_SLOTS && (
                <button onClick={addSlot} data-testid="create-njm-collaboration-slot-add"
                  className="w-full rounded-xl border border-dashed border-black/20 bg-surface2/40 py-3 font-mont text-xs font-bold text-ink hover:bg-surface2/70 transition-colors">
                  <Plus className="inline h-3.5 w-3.5 mr-1" /> Add another role
                </button>
              )}
            </div>
            {errors.slots && <div className="mt-1.5 text-[11px] text-red-500" data-testid="create-njm-collaboration-slots-error">{errors.slots}</div>}
            {errors.fees && <div className="mt-1.5 text-[11px] text-red-500" data-testid="create-njm-collaboration-fees-error">{errors.fees}</div>}
            {errors.roleDescription && <div className="mt-1.5 text-[11px] text-red-500">{errors.roleDescription}</div>}
          </div>

          <div className="h-px bg-black/8" />

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="create-njm-collaboration-budget-block">
            <div className="font-mont text-[10px] font-extrabold uppercase tracking-[0.14em] text-amber-800">Staff-only · Talent Budget</div>
            {adminDataError && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 font-mont text-[11px] text-red-600" data-testid="create-njm-collaboration-budget-error">
                Couldn't load the current budget — Save is disabled until this loads, to avoid overwriting it.
                <button type="button" onClick={loadAdminBudget} data-testid="create-njm-collaboration-budget-retry" className="font-bold underline shrink-0">Retry</button>
              </div>
            )}
            <div className="mt-2.5">
              <label className={label}>Total Talent Budget <span className="text-hairline-strong">optional (₹)</span></label>
              <input type="number" min="0" value={totalBudget} disabled={isEdit && !adminDataLoaded} onChange={(e) => setTotalBudget(e.target.value)} placeholder="e.g. 150000" data-testid="create-njm-collaboration-total-budget"
                className="mt-1.5 w-full rounded-lg border border-amber-300 bg-white px-3.5 py-2.5 font-mont text-sm text-ink placeholder:text-amber-400 focus:outline-none focus:border-amber-500 disabled:opacity-50" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div>
                <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-amber-700">Planned</div>
                <div className="font-mont text-[13px] font-bold text-ink" data-testid="create-njm-collaboration-planned">{fmt(plannedBudget)}</div>
              </div>
              <div>
                <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-amber-700">Committed</div>
                <div className="font-mont text-[13px] font-bold text-ink" data-testid="create-njm-collaboration-committed">{fmt(committed)}</div>
              </div>
              <div>
                <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-amber-700">Remaining</div>
                <div className="font-mont text-[13px] font-bold text-ink" data-testid="create-njm-collaboration-remaining">{fmt(remaining)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-3 border-t border-black/8 bg-surface2/40 px-6 sm:px-7 py-4">
          <button onClick={onClose} data-testid="create-njm-collaboration-cancel" className="font-mont text-xs font-semibold text-ink-soft hover:text-ink transition-colors">Cancel</button>
          <button onClick={submit} disabled={submitting || !adminDataLoaded} data-testid="create-njm-collaboration-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 font-mont text-xs font-bold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
            {/* Code-review fix (2026-10, Cursor audit P4): a failed admin-budget fetch used to
                leave this spinning forever (bound to `!adminDataLoaded`, which stays true on
                failure too) — now it only spins while actually submitting or still loading,
                never while sitting disabled on a failure (the Retry banner covers that case). */}
            {submitting || (!adminDataLoaded && !adminDataError) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {isEdit ? "Save Changes" : "Create NJM Collaboration →"}
          </button>
        </div>
      </div>
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}
